/* fm-a11y.js — keyboard/focus repairs for the purchase path.
 * Added Aug 19 2026 during the counsel-directed accessibility pass.
 *
 * Problem: the cart drawer is a real modal (role="dialog" aria-modal="true"),
 * but focus never enters it. Dawn's cart-drawer runs trapFocus() from a
 * one-shot 'transitionend' listener; on this theme that event does not fire,
 * so a keyboard user who presses Add to Cart is left on <body> behind an open
 * modal, and Tab keeps walking the page underneath it (WCAG 2.4.3 Focus Order,
 * 2.1.2 No Keyboard Trap in reverse).
 *
 * This is defensive: it only acts when focus is NOT already inside the drawer,
 * so if the theme's own trapFocus ever starts working it wins and this no-ops.
 */
(function () {
  'use strict';

  var lastTrigger = null;

  document.addEventListener(
    'pointerdown',
    function (e) {
      var t = e.target && e.target.closest
        ? e.target.closest('.product-form__submit, [name="add"], .cart-icon, #cart-icon-bubble, a[href$="/cart"]')
        : null;
      if (t) lastTrigger = t;
    },
    true
  );

  function focusInto(drawer) {
    if (document.activeElement && drawer.contains(document.activeElement)) return;

    var inner = drawer.querySelector('.drawer__inner');
    var target =
      drawer.querySelector('.drawer__close') ||
      inner ||
      drawer;

    // Prefer the theme's own focus trap so Tab stays inside the dialog.
    if (typeof window.trapFocus === 'function') {
      try {
        window.trapFocus(document.getElementById('CartDrawer') || inner || drawer, target);
        if (document.activeElement && drawer.contains(document.activeElement)) return;
      } catch (err) {
        /* fall through to the plain focus below */
      }
    }

    if (target && !target.hasAttribute('tabindex') && target === inner) {
      target.setAttribute('tabindex', '-1');
    }
    if (target && target.focus) target.focus();
  }

  function releaseFocus(drawer) {
    if (typeof window.removeTrapFocus === 'function') {
      try {
        window.removeTrapFocus(lastTrigger || undefined);
        return;
      } catch (err) {
        /* fall through */
      }
    }
    if (lastTrigger && document.contains(lastTrigger) && lastTrigger.focus) lastTrigger.focus();
  }

  function watch(drawer) {
    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive === wasActive) return;
      wasActive = isActive;
      // let the theme's own handlers run first
      setTimeout(function () {
        if (isActive) focusInto(drawer);
        else releaseFocus(drawer);
      }, 60);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    document.querySelectorAll('cart-drawer').forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* ── Sep 10 2026: site-wide runtime repairs from the full-site axe sweep ──
 * Decorative images with no alt, dead icon links, keyboard-unreachable
 * horizontal scrollers, and Kaching bundle bars whose selected state is
 * invisible to assistive tech. All idempotent; re-run on DOM changes. */
(function () {
  'use strict';
  function repair(root) {
    root = root || document;
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', ''); img.setAttribute('aria-hidden', 'true');
    });
    root.querySelectorAll('a[href="#"][aria-label=""], a[href="#"]:not([aria-label]):empty').forEach(function (a) {
      if (!a.textContent.trim() && !a.querySelector('img[alt]:not([alt=""])')) {
        a.setAttribute('aria-hidden', 'true'); a.setAttribute('tabindex', '-1');
      }
    });
    [['.pt-grid', 'Product details, scrollable'], ['.video-gallery-container', 'Video gallery, scrollable']].forEach(function (p) {
      root.querySelectorAll(p[0]).forEach(function (el) {
        if (!el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '0'); el.setAttribute('role', 'region'); el.setAttribute('aria-label', p[1]); }
      });
    });
    root.querySelectorAll('.kaching-bundles__bar-main[role="button"]').forEach(function (b) {
      var bar = b.closest('.kaching-bundles__bar') || b;
      var on = /selected|active|checked/i.test(bar.className) || bar.getAttribute('aria-checked') === 'true' || !!bar.querySelector('input:checked');
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  var t;
  function schedule() { clearTimeout(t); t = setTimeout(function () { repair(); }, 120); }
  function start() {
    repair();
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    document.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('.kaching-bundles__bar')) setTimeout(repair, 50); }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
